/**
 * BasePanelLayout — layout base compartilhado entre AdminLayout e SuperAdminLayout.
 * Usa SidebarNav com NavConfig para renderizar grupos colapsáveis.
 */
import { useState, lazy, Suspense } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useTheme } from "@/hooks/use-theme";
import { ChevronLeft, LogOut, Sun, Moon } from "lucide-react";
import { cn } from "@/lib/utils";
import { ContextSwitcher } from "@/components/layout/ContextSwitcher";
import NotificationBell from "@/components/NotificationBell";
import { SidebarNav, type SidebarColorScheme } from "@/components/layout/SidebarNav";
import { TOURS_ENABLED } from "@/lib/tours-enabled";

const ScreenTour = lazy(() => import("@/components/tour/ScreenTour"));
import { CommandPalette } from "@/components/layout/CommandPalette";
import type { NavConfig, NavItem } from "@/config/navigation";

export interface PanelColorScheme {
  activeBg: string;
  activeText: string;
  border: string;
  mobileActive: string;
}

export interface BasePanelLayoutProps {
  navConfig: NavConfig;
  colorScheme: PanelColorScheme;
  sidebarHeader: (collapsed: boolean) => React.ReactNode;
  mobileHeader: React.ReactNode;
  darkBg?: string;
  lightBg?: string;
}

export default function BasePanelLayout({
  navConfig,
  colorScheme,
  sidebarHeader,
  mobileHeader,
  darkBg,
  lightBg,
}: BasePanelLayoutProps) {
  const { signOut, user } = useAuth();
  const { isDark, setTheme } = useTheme();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const sidebarColors: SidebarColorScheme = {
    activeBg: colorScheme.activeBg,
    activeText: colorScheme.activeText,
  };

  const asideStyle =
    darkBg || lightBg
      ? { background: isDark ? darkBg : lightBg }
      : undefined;

  const allItems: NavItem[] = navConfig.groups.flatMap((g) => g.items).filter(
    (item) => !item.restrictToEmail || item.restrictToEmail === user?.email
  );

  function isActive(item: NavItem): boolean {
    const p = location.pathname;
    if (p === item.route) return true;
    const segments = item.route.split("/").filter(Boolean);
    if (segments.length <= 1) return false;
    return p.startsWith(item.route);
  }

  return (
    <div className="min-h-screen flex bg-background">
      <CommandPalette
        config={navConfig}
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        colorScheme={sidebarColors}
      />

      {/* Desktop Sidebar */}
      <aside
        data-tour="painel-sidebar"
        className={cn(
          "hidden lg:flex flex-col transition-all duration-200",
          `border-r ${colorScheme.border} bg-card`,
          collapsed ? "w-16" : "w-60"
        )}
        style={asideStyle}
      >
        {/* Header */}
        <div className={cn("flex items-center justify-between h-14 px-3 border-b", colorScheme.border)}>
          {sidebarHeader(collapsed)}
          <button
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
            className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            <ChevronLeft className={cn("h-4 w-4 transition-transform", collapsed && "rotate-180")} />
          </button>
        </div>

        {/* Context Switcher */}
        <div className={cn("border-b", colorScheme.border)}>
          <ContextSwitcher collapsed={collapsed} />
        </div>

        {/* Nav com grupos colapsáveis */}
        <SidebarNav
          config={navConfig}
          collapsed={collapsed}
          colorScheme={sidebarColors}
        />

        {/* Bottom */}
        <div className={cn("px-2 pb-3 space-y-1 border-t pt-3", colorScheme.border)}>
          {!collapsed && (
            <button
              onClick={() => setPaletteOpen(true)}
              className="flex items-center justify-between w-full px-3 py-2 rounded-lg text-[12px] text-muted-foreground/60 hover:text-muted-foreground hover:bg-muted transition-colors"
              aria-label="Abrir paleta de comandos"
            >
              <span>Busca rápida</span>
              <kbd className="text-[10px] bg-muted px-1.5 py-0.5 rounded">⌘K</kbd>
            </button>
          )}
          <button
            onClick={() => setTheme(isDark ? "light" : "dark")}
            aria-label="Alternar tema"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors w-full"
          >
            {isDark
              ? <Sun className="h-[18px] w-[18px] shrink-0" />
              : <Moon className="h-[18px] w-[18px] shrink-0" />}
            {!collapsed && "Tema"}
          </button>
          <button
            onClick={signOut}
            aria-label="Sair da conta"
            data-testid="logout-button"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] text-destructive hover:bg-destructive/10 transition-colors w-full"
          >
            <LogOut className="h-[18px] w-[18px] shrink-0" />
            {!collapsed && "Sair"}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Mobile Top Header */}
        {/* O seletor de contexto vive aqui, e não só na sidebar: abaixo de `lg`
            o <aside> não é renderizado e estes painéis não têm menu lateral —
            sem isto, quem tem perfil duplo entra no painel administrativo pelo
            celular e não tem caminho de volta para o painel Cliente. */}
        <header className={cn("h-14 border-b flex items-center gap-2 px-4 lg:px-6 bg-card shrink-0", colorScheme.border)}>
          <div className="flex items-center gap-2 lg:hidden">
            {mobileHeader}
          </div>
          <div className="ml-auto flex items-center gap-1">
            <div className="lg:hidden">
              <ContextSwitcher compact />
            </div>
            {/* O sino vive aqui, e este é o ÚNICO da tela — o mesmo lugar que o
                AppLayout do painel Cliente usa. Sem ele, `varrer_anomalias()` e
                `stripe-webhook-retry` gravavam notificação para todo
                superadministrador com link `/superadmin`, e o painel que abre
                nesse link não tinha onde lê-la: o aviso de "webhook de pagamento
                abandonado" ficava numa tabela que nenhuma tela deste painel
                consulta. Fica FORA do `lg:hidden` de propósito — o defeito
                existia nas duas larguras, não só no celular. */}
            <NotificationBell />
          </div>
        </header>

        {/* Mobile Horizontal Nav */}
        <nav
          aria-label="Navegação mobile"
          className={cn("lg:hidden flex overflow-x-auto border-b bg-card px-2 gap-1 shrink-0", colorScheme.border)}
        >
          {allItems.map((item) => {
            const active = isActive(item);
            return (
              <Link
                key={item.id}
                to={item.route}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium whitespace-nowrap transition-colors border-b-2",
                  active
                    ? colorScheme.mobileActive
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                <item.icon className="h-3.5 w-3.5" aria-hidden="true" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <div data-tour="painel-conteudo" className="max-w-7xl mx-auto animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Sem lançador flutuante nos painéis internos, POR DECISÃO.
          
          Aqui o gatilho de ajuda é o "?" ao lado do título de cada tela, e um
          só. O lançador flutuante abria exatamente a mesma ajuda da tela por um
          segundo botão, e dois pontos de interrogação numa tela ensinam a não
          usar nenhum.

          O tour continua montado: ele não é um segundo gatilho — não tem botão
          próprio e é disparado pela tela, pelo painel de ajuda ou por `?tour=`. */}
      {TOURS_ENABLED && (
        <Suspense fallback={null}>
          <ScreenTour />
        </Suspense>
      )}
    </div>
  );
}
